import { getElement } from '../Utility/Dom'
import { IS_ACTIVE } from '../Utility/State'

export interface ToastOptions {
  duration?: number
  message: string
  title?: string
  type?: ToastType
  withIcon?: boolean
}

export type ToastType = 'error' | 'info' | 'success' | 'warning'

class ToastService {
  private static readonly TRANSITION_MS = 300

  private currentToast: HTMLElement | null = null
  private readonly defaultDuration: number = 3000
  private hideTimeout: null | ReturnType<typeof setTimeout> = null
  private removeTimeout: null | ReturnType<typeof setTimeout> = null

  public error(message: string, duration?: number): void {
    this.show({ duration, message, type: 'error' })
  }

  public hide(): void {
    if (this.currentToast === null) return

    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }

    const toast = this.currentToast
    this.currentToast = null

    toast.classList.remove(IS_ACTIVE)

    const remove = (): void => {
      if (toast.parentNode !== null) toast.remove()
    }

    toast.addEventListener('transitionend', remove, { once: true })
    this.removeTimeout = setTimeout(remove, ToastService.TRANSITION_MS)
  }

  public info(message: string, duration?: number): void {
    this.show({ duration, message, type: 'info' })
  }

  public show(options: ToastOptions): void {
    const { duration = this.defaultDuration, message, title, type = 'info', withIcon = true } = options

    this.clearCurrentToast()

    const toast = this.createToast({ message, title, type, withIcon })
    document.body.appendChild(toast)
    toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite')
    this.currentToast = toast

    // Join the top layer so the toast renders above modal <dialog> backdrops.
    if (typeof toast.showPopover === 'function') toast.showPopover()

    requestAnimationFrame((): void => {
      toast.classList.add(IS_ACTIVE)
    })

    this.hideTimeout = setTimeout((): void => this.hide(), duration)
  }

  public showFromElement(identifier: string, overrides: Partial<ToastOptions> = {}): void {
    const element = getElement(identifier)
    if (element === null) return

    const { toastMessage, toastTitle, toastType } = element.dataset
    const message = overrides.message ?? toastMessage ?? ''
    if (message === '') return

    this.show({
      duration: overrides.duration,
      message,
      title: overrides.title ?? toastTitle,
      type: overrides.type ?? (toastType as ToastType | undefined),
      withIcon: overrides.withIcon,
    })
  }

  public success(message: string, duration?: number): void {
    this.show({ duration, message, type: 'success' })
  }

  public warning(message: string, duration?: number): void {
    this.show({ duration, message, type: 'warning' })
  }

  private clearCurrentToast(): void {
    if (this.currentToast === null) return

    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
    if (this.removeTimeout !== null) {
      clearTimeout(this.removeTimeout)
      this.removeTimeout = null
    }
    this.currentToast.remove()
    this.currentToast = null
  }

  private createToast(options: Pick<ToastOptions, 'title' | 'withIcon'> & Required<Pick<ToastOptions, 'message' | 'type'>>): HTMLElement {
    const { message, title, type, withIcon } = options

    const toast = document.createElement('div')
    toast.className = `box box--toast box--${type}`
    toast.setAttribute('popover', 'manual')
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status')

    if (withIcon === true) {
      toast.classList.add('box--icon')
      const icon = document.createElement('span')
      icon.className = 'box__icon'
      icon.setAttribute('aria-hidden', 'true')
      toast.appendChild(icon)
    }

    const body = document.createElement('div')
    body.className = 'box__body'

    if (title !== undefined && title !== '') {
      const titleElement = document.createElement('div')
      titleElement.className = 'box__title'
      titleElement.textContent = title
      body.appendChild(titleElement)
    }

    const messageElement = document.createElement('div')
    messageElement.className = 'box__message'
    messageElement.textContent = message
    body.appendChild(messageElement)

    toast.appendChild(body)

    return toast
  }
}

export default new ToastService()
